import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  DatePicker,
  Select,
  Button,
  Spin,
  Empty,
  Card,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  PrinterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { toPng } from "html-to-image";
import Layout from "../components/layout/Layout";
import { MachineContext } from "../context/MachineContext";

const WorkingHoursPage = () => {
  const { machines: machineList } = useContext(MachineContext);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  // const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState({ machine: null, dateRange: [] });
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_MONGO_URL;

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setLoading(true);
    fetch(`${apiUrl}/api/dailyWorkingHours`)
      .then((res) => res.json())
      .then((data) => {
        setData(data);
        const todayData = data.filter((item) => item.date.startsWith(today));
        setFilteredData(todayData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [apiUrl]);

  const handleFilter = () => {
    const { machine, dateRange } = filters;
    let result = data;

    if (machine) result = result.filter((item) => item.machineId === machine);
    if (dateRange.length === 2) {
      const [start, end] = dateRange;
      result = result.filter(
        (item) => new Date(item.date) >= start && new Date(item.date) <= end
      );
    }

    // Veriyi gruplama ve toplam çalışma saatlerini hesaplama
    const groupedData = result.reduce((acc, item) => {
      const key = `${item.machineId}-${
        new Date(item.date).toISOString().split("T")[0]
      }`;
      if (!acc[key]) {
        acc[key] = { ...item };
      } else {
        acc[key].dailyWorkingHours += item.dailyWorkingHours;
      }
      return acc;
    }, {});

    setFilteredData(Object.values(groupedData));
  };

  const resetFilters = () => {
    setFilters({ machine: null, dateRange: [] });
    const today = new Date().toISOString().split("T")[0];
    const todayData = data.filter((item) => item.date.startsWith(today));
    setFilteredData(todayData);
  };
  const calculateKPIs = () => {
    const calculateTotalWorkingHours = (data) => {
      let totalMinutes = 0;

      data.forEach((record) => {
        const timeParts = record.dailyWorkingHours.split(":");
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        totalMinutes += hours * 60 + minutes; // Tüm süreyi dakikaya çevirip topluyoruz
      });

      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;

      return `${totalHours} saat ${remainingMinutes} dakika`;
    };

    const totalWorkingHours = calculateTotalWorkingHours(filteredData);

    // Makineye göre süreleri dakikaya çevirip toplama işlemi
    const machineAverages = filteredData.reduce((acc, record) => {
      const timeParts = record.dailyWorkingHours.split(":");
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const totalMinutes = hours * 60 + minutes;

      if (!acc[record.machineId]) {
        acc[record.machineId] = 0;
      }
      acc[record.machineId] += totalMinutes; // Dakika cinsinden süreleri topluyoruz
      return acc;
    }, {});

    // En uzun süre çalışan makineyi bulma
    const mostEfficientMachine = Object.entries(machineAverages).reduce(
      (max, [machine, totalMinutes]) =>
        totalMinutes > max.totalMinutes ? { machine, totalMinutes } : max,
      { machine: null, totalMinutes: 0 }
    );

    const totalHours = Math.floor(mostEfficientMachine.totalMinutes / 60);
    const remainingMinutes = mostEfficientMachine.totalMinutes % 60;

    const getMachineNameById = (id) => {
      const machine = machineList.find((machine) => machine._id === id);
      return machine ? machine.name + "/" + machine.code : id;
    };

    return {
      totalWorkingHours,
      mostEfficientMachine: `${getMachineNameById(
        mostEfficientMachine.machine
      )} - ${totalHours} saat ${remainingMinutes} dakika`,
    };
  };

  const { totalWorkingHours, mostEfficientMachine } = calculateKPIs();

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      return alert("Export edilecek veri bulunamadı.");
    }
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Working Hours");
    XLSX.writeFile(workbook, "working_hours.xlsx");
  };

  const exportToPng = () => {
    const tableElement = document.getElementById("working-hours-table");
    toPng(tableElement)
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "working_hours.png";
        link.href = dataUrl;
        link.click();
      })
      .catch((error) => {
        console.error("PNG dışa aktarma hatası:", error);
      });
  };

  const calculateChange = (current, previous) => {
    if (previous === 0 || !previous) return null; // Önceki gün verisi yoksa değişim hesaplanmaz
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1); // Ondalık hassasiyeti
  };

  const getMachineNameById = (id) => {
    const machine = machineList.find((machine) => machine._id === id);
    return machine ? machine.name + "/" + machine.code : id; // Eğer isim bulunamazsa ID döner
  };

  const columns = [
    {
      title: "Makine",
      dataIndex: "machineId",
      key: "machineId",
      render: (id) => getMachineNameById(id),
    },
    { title: "Operatör", dataIndex: "operatorId", key: "operatorId" },
    {
      title: "Çalışma Saati",
      dataIndex: "dailyWorkingHours",
      key: "dailyWorkingHours",
      render: (hours, record) => {
        // String formatını saat ve dakikaya dönüştürme
        const timeParts = hours.split(":"); // ["6", "24", "07.447000"]
        const displayHours = parseInt(timeParts[0], 10);
        const displayMinutes = parseInt(timeParts[1], 10);

        const previousDayData = data.find(
          (item) =>
            item.machineId === record.machineId &&
            new Date(item.date).getTime() ===
              new Date(record.date).getTime() - 86400000
        );
        const previousHours = previousDayData?.dailyWorkingHours || 0;
        const change = calculateChange(hours, previousHours);

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {`${displayHours} saat ${displayMinutes} dakika`}
            {change && (
              <Tooltip title={`Değişim: %${change}`}>
                <span
                  style={{ marginLeft: 8, color: change > 0 ? "green" : "red" }}
                >
                  {change > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} %{" "}
                  {Math.abs(change)}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Tarih",
      dataIndex: "date",
      key: "date",
      render: (text) => new Date(text).toLocaleDateString("tr-TR"),
    },
  ];

  return (
    <Layout>
      <div className="pt-16 ml-64">
        {/* KPI Kartları */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={8}>
            <Card title="Toplam Çalışma Saati" bordered>
              {totalWorkingHours}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="En Verimli Makine" bordered>
              {mostEfficientMachine}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Duraklama Oranı" bordered>
              % (Veri yok)
            </Card>
          </Col>
        </Row>

        {/* Filtreleme ve Export Seçenekleri */}
        <div
          style={{ marginBottom: 20, display: "flex", alignItems: "center" }}
        >
          <Select
            placeholder="Makine Seçiniz"
            style={{ width: 200 }}
            value={filters.machine} // Makine ID'sini filtreleme için kullanıyoruz
            onChange={(value) => setFilters({ ...filters, machine: value })} // ID'yi filtreye yazıyoruz
          >
            {machineList.map((machine) => (
              <Select.Option key={machine._id} value={machine._id}>
                {machine.name + " / " + machine.code}{" "}
                {/* Kullanıcıya görünen makine adı */}
              </Select.Option>
            ))}
          </Select>
          <DatePicker.RangePicker
            placeholder={["Başlangıç", "Bitiş"]}
            style={{ marginLeft: 10 }}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button
            type="primary"
            style={{ marginLeft: 10 }}
            onClick={handleFilter}
          >
            Filtrele
          </Button>
          <Button style={{ marginLeft: 10 }} onClick={resetFilters}>
            Temizle
          </Button>
          <Button style={{ marginLeft: 10 }} onClick={exportToExcel}>
            <DownloadOutlined /> Excel
          </Button>
          <Button style={{ marginLeft: 10 }} onClick={exportToPng}>
            <DownloadOutlined /> PNG
          </Button>
          <Button style={{ marginLeft: 10 }} onClick={() => window.print()}>
            <PrinterOutlined /> Yazdır
          </Button>
        </div>

        {/* Tablo */}
        {loading ? (
          <Spin size="large" style={{ display: "block", marginTop: 50 }} />
        ) : filteredData.length === 0 ? (
          <Empty
            description="Gösterilecek veri bulunamadı"
            style={{ marginTop: 50 }}
          />
        ) : (
          <div id="working-hours-table">
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WorkingHoursPage;
